'use client';

import type { ComponentProps } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AvatarEditorRef } from 'react-avatar-editor';
import ReactAvatarEditor from 'react-avatar-editor';
import styled from 'styled-components';
import type { AvatarShapes } from '@frameio/vapor';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Inline,
  Slider,
  Spinner,
  Stack,
  Text,
  theme,
} from '@frameio/vapor';
import {
  IconFolderOpen,
  IconTrash,
  IconUploads,
  IconZoomIn,
  IconZoomOut,
} from '@frameio/vapor-icons';
import type { FileDropZoneProps } from '@frameio/vapor-migration';
import { FileDropZone } from '@frameio/vapor-migration';
import { invalidFileTypeFragment, isValidFileType } from '../utils';
import TEXT from './generated/TEXT/AvatarEditorDialog';

export const DEFAULT_ACCEPTED_FILETYPES = 'image/gif, image/jpeg, image/png';

TEXT.DEFINE(
  {
    /** Cancel button label */
    cancel: 'Cancel',
    /** Delete image button label */
    delete: 'Delete image',
    /** Dropzone instruction text */
    dropzone: 'Drag and drop or click to upload image',
    /** Dialog label */
    label: 'Edit image',
    /** Save button label */
    save: 'Save',
    /** Select new image button label */
    select: 'Select new image',
    /** Error message when invalid file type is uploaded */
    invalidFiletypeError: (supportedTypes: string) => {
      const formattedTypes = invalidFileTypeFragment(supportedTypes);
      return `Upload failed. File must be one of the following types: ${formattedTypes}`;
    },
    /** Loading spinner label */
    spinnerLabel: 'Avatar Editor loading',
  },
  { invalidFileTypeFragment }
);

const canvasBackground = {
  backgroundImage: `${theme.colors.background.canvas}`,
  backgroundSize: `${theme.spaces[24]} ${theme.spaces[24]}`,
  backgroundPosition: `0 0, ${theme.spaces[12]} 0, ${theme.spaces[12]} calc(${theme.spaces[12]} * -1), 0 ${theme.spaces[12]}`,
  backgroundColor: `${theme.colors.components.avatarEditorDialog.background}`,
};

const INDICATOR_SIZE = 352;

const StyledDropzone = styled(FileDropZone)``;
const DropIndicator = styled(Box)<{ $shape: AvatarShapes }>`
  border-radius: ${(p) => (p.$shape === 'circle' ? '50%' : '20px')};
  cursor: pointer;
  height: ${INDICATOR_SIZE}px;
  width: ${INDICATOR_SIZE}px;

  ${StyledDropzone}.focus, ${StyledDropzone}:focus[data-focus-visible-added] {
    & {
      /* prettier-ignore */
      box-shadow: inset 0 0 0 ${theme.spaces[2]} ${theme.colors.border
        .interactive.selected};
    }
  }
`;

const EDITOR_WIDTH = 352;
const EDITOR_HEIGHT = 352;
const EDITOR_BORDER = { x: 64, y: 16 };

type AvatarEditorDialogProps = {
  acceptedFileTypes?: string;
  avatarSrc?: string;
  dropzoneLabel?: string;
  editLabel?: string;
  name?: string;
  onSave?: (blob: Blob | null) => void;
  shape: AvatarShapes;
  validationErrorMessage?: string;
} & Pick<FileDropZoneProps, 'validate'> &
  Partial<ComponentProps<typeof Dialog>>;

const FadeIn = styled(Box)<{ $isVisible: boolean }>`
  opacity: ${(p) => (p.$isVisible ? 1 : 0)};
  transition-duration: ${theme.transitions.durations[150]};
  transition-property: opacity;
  transition-timing-function: ${theme.transitions.eases.easeOut};
`;

const obtainBaseScale = (
  src: string,
  setBaseScale: (arg0: number) => void,
  onError: () => void
) => {
  const testImg = new Image();
  testImg.onload = () => {
    const ratio =
      testImg.width > testImg.height
        ? testImg.height / testImg.width
        : testImg.width / testImg.height;
    setBaseScale(ratio);
  };
  testImg.onerror = onError;
  testImg.src = src;
};

export function AvatarEditorDialog({
  acceptedFileTypes: acceptedFileTypesProp,
  avatarSrc,
  editLabel = TEXT.label,
  isShowing = false,
  name,
  onRequestClose,
  onSave,
  shape,
  validate,
  validationErrorMessage,
}: AvatarEditorDialogProps) {
  const initialState = {
    scale: 0,
    isDirty: false,
    isSaving: false,
    editorImage: avatarSrc,
    isImageReady: false,
    hasEntered: false,
  };

  const [baseScale, setBaseScale] = useState(0);
  const [scale, setScale] = useState(initialState.scale);
  const [isDirty, setIsDirty] = useState(initialState.isDirty);
  const [isSaving, setIsSaving] = useState(initialState.isSaving);

  const editorRef = useRef<AvatarEditorRef>(null);

  /**
   * Copy the value of props.image to local state so that we can,
   * 1. preserve the image's appearance in the editor after changes have been
   * saved, but before the dialog has completed exiting; and,
   * 2. delete and replace the current avatar image
   * 3. allow the image to be removed/replaced in the editor, but still allow
   * for the user to bail out and cancel their changes
   */
  const [editorImage, setEditorImage] = useState(initialState.editorImage);
  const [isImageReady, setIsImageReady] = useState(initialState.isImageReady);
  const [hasEntered, setHasEntered] = useState(initialState.hasEntered);

  // If an image had failed to upload and can no longer be loaded:
  // end the loading sequence and ensure the editor is reset.
  const handleImageLoadFailure = () => {
    setIsImageReady(true);
    setEditorImage(undefined);
  };

  useEffect(() => {
    setEditorImage(avatarSrc);
  }, [avatarSrc]);

  useEffect(() => {
    if (editorImage) {
      // If the source image can't be loaded (e.g. a broken/expired URL or a
      // failed upload), fall back to the empty upload state instead of leaving
      // the editor stuck on a spinner that never resolves.
      obtainBaseScale(editorImage, setBaseScale, handleImageLoadFailure);
    }

    return () => {
      if (editorImage) URL.revokeObjectURL(editorImage);
    };
  }, [editorImage, setBaseScale]);

  const resetEditor = () => {
    setScale(initialState.scale);
    setIsDirty(initialState.isDirty);
    setIsSaving(initialState.isSaving);
    setIsImageReady(initialState.isImageReady);
    setEditorImage(initialState.editorImage);
    setHasEntered(initialState.hasEntered);
  };

  const handleScale = (n: number) => {
    setScale(n);
    setIsDirty(true);
  };

  const handleChange = (file: File) => {
    if (file) {
      setEditorImage(URL.createObjectURL(file));
      setScale(0);
      setIsDirty(true);
    }
  };

  const handleSave = useCallback(() => {
    if (!onSave) return;
    setIsSaving(true);

    try {
      if (editorRef.current) {
        /**
         * Using `getImage` from ReactAvatarEditor to get the original image
         * instead of getting the downscaled image based on the size of canvas
         */
        editorRef.current.getImage().toBlob(onSave);
      } else {
        onSave(null);
      }
    } catch {
      setIsSaving(false);
    }
  }, [onSave]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const borderRadius = shape === 'circle' ? EDITOR_WIDTH / 2 : 20;

  const acceptedFileTypes = acceptedFileTypesProp || DEFAULT_ACCEPTED_FILETYPES;
  const invalidTypeMessage =
    validationErrorMessage ||
    String(TEXT.invalidFiletypeError(acceptedFileTypes));

  /**
   * Note: wildcard `acceptedFileTypes` will need a custom `validate` function
   * passed as a property. A `validationErrorMessage` will also likely
   * be necessary.
   *
   * eg.
   * acceptedFileTypes="image/*"
   * validate={({ type }) => /^(image)\//.test(type);}
   * validationErrorMessage="Oops. Only images are supported."
   *
   */
  const validateFileType = (file: File) => {
    if (typeof validate === 'function') {
      return validate(file);
    }

    return isValidFileType(file, acceptedFileTypes);
  };

  return (
    <Dialog
      aria-label={editLabel}
      data-testid="fio-avatar-editor-dialog"
      enableClickOutsideToClose={false}
      isShowing={isShowing}
      onEntered={() => setHasEntered(true)}
      onExited={resetEditor}
      onRequestClose={onRequestClose}
      style={{ overflow: 'hidden' }}
      zIndex={1502}
    >
      <Box overflow="hidden">
        <Box display="flex" position="relative">
          <StyledDropzone
            ref={fileInputRef}
            accept={acceptedFileTypes}
            alignItems="center"
            allowMultiple={false}
            backgroundColor="activatedSolid"
            behavior={editorImage ? 'display' : 'button'}
            data-testid="fio-avatar-editor-dialog--file-input"
            display="flex"
            invalidTypeMessage={invalidTypeMessage}
            justifyContent="center"
            name={name}
            onChange={(f) => handleChange(f as File)}
            validate={validateFileType}
            width="100%"
          >
            {({ canDrop }) =>
              editorImage ? (
                <>
                  <FadeIn
                    $isVisible={isImageReady}
                    display="flex"
                    style={{ height: EDITOR_HEIGHT + EDITOR_BORDER.y * 2 }}
                  >
                    {hasEntered && baseScale && (
                      <ReactAvatarEditor
                        ref={editorRef}
                        border={[EDITOR_BORDER.x, EDITOR_BORDER.y]}
                        borderRadius={borderRadius}
                        color={[44, 48, 65, 0.3]}
                        crossOrigin="anonymous"
                        height={EDITOR_HEIGHT}
                        image={editorImage}
                        onImageReady={() => setIsImageReady(true)}
                        onLoadFailure={handleImageLoadFailure}
                        onPositionChange={() => {
                          if (!isDirty) {
                            setIsDirty(true);
                          }
                        }}
                        scale={baseScale + scale / 100}
                        style={canvasBackground}
                        width={EDITOR_WIDTH}
                      />
                    )}
                  </FadeIn>
                  <Spinner
                    aria-label={TEXT.spinnerLabel}
                    centered={true}
                    delay={400}
                    isLoading={!isImageReady}
                    size="small"
                  />
                </>
              ) : (
                <Box paddingY={16}>
                  <DropIndicator
                    $shape={shape}
                    alignItems="center"
                    backgroundColor="secondary"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    textAlign="center"
                  >
                    <Box
                      color={canDrop ? 'primary' : 'secondary'}
                      paddingX={64}
                    >
                      <IconUploads />
                      <Box paddingTop={4}>
                        <Text variant="primary">{TEXT.dropzone}</Text>
                      </Box>
                    </Box>
                  </DropIndicator>
                </Box>
              )
            }
          </StyledDropzone>
          {editorImage && (
            <FadeIn $isVisible={isImageReady}>
              <Box
                position="absolute"
                right={theme.spaces[16]}
                top={theme.spaces[16]}
              >
                <Stack gap={8}>
                  <IconButton
                    color="neutral"
                    icon={<IconFolderOpen />}
                    label={TEXT.select}
                    onClick={() => {
                      /**
                       * @note Forward click events on this button as clicks to the file input
                       * element. This handles both pointer clicks as well as "Enter"
                       * and spacebar keypresses for accessibility purposes.
                       */
                      fileInputRef.current?.click();
                    }}
                    type="button"
                  />
                  <IconButton
                    color="neutral"
                    data-testid="fio-avatar-editor-dialog--delete-button"
                    icon={<IconTrash />}
                    label={TEXT.delete}
                    onClick={() => {
                      setEditorImage(undefined);
                      setIsDirty(true);
                    }}
                    type="button"
                  />
                </Stack>
              </Box>
            </FadeIn>
          )}
        </Box>
        <Box display="flex" justifyContent="space-between" padding={16}>
          <Box
            alignItems="center"
            display="flex"
            flexBasis="55%"
            paddingRight={16}
          >
            {isImageReady && editorImage && (
              <>
                <IconZoomOut />
                <Box flexGrow="1" paddingLeft={16} paddingRight={12}>
                  <Slider
                    max={100}
                    min={0}
                    onChange={handleScale}
                    showTooltip={false}
                    step={1}
                    value={scale}
                  />
                </Box>
                <IconZoomIn />
              </>
            )}
          </Box>
          <Inline gapX={8}>
            <Button
              data-testid="fio-avatar-editor-dialog--cancel-button"
              onClick={onRequestClose}
              type="button"
            >
              {TEXT.cancel}
            </Button>
            <Button
              color="primary"
              data-testid="fio-avatar-editor-dialog--save-button"
              disabled={!isDirty || isSaving || !isImageReady}
              onClick={handleSave}
              type="button"
            >
              {TEXT.save}
            </Button>
          </Inline>
        </Box>
      </Box>
    </Dialog>
  );
}
